import { defineStore } from 'pinia'
import { MilliSeconds } from '@/Enums/MilliSeconds'
import { useStorage, useTimeoutPoll } from '@vueuse/core'
import { isEmpty, isEqual } from 'lodash-es'
import logger from '@/Helpers/Logger'
import Queue from 'queue'

export const useAuthStore = defineStore('auth-store', {
  state: () => ({
    user: {} as IPChat.Auth.User,
    company: {} as IPChat.Auth.Company,
    companies: [] as IPChat.Company[],
    myDepartments: [] as IPChat.Department[],
    departments: [] as IPChat.Department[],
    tags: [] as IPChat.Tag[],
    presenceList: [] as IPChat.Auth.PresenceList[],
    socketIsReady: false,
    config: {
      notification: {
        alert: useStorage('notificationAlert', true, localStorage),
        sound: useStorage('notificationSound', true, localStorage),
      },
    },
  }),
  getters: {
    isAdmin: (state) => state.company.pivot?.role === 'admin',
    isAll: (state) => state.company.pivot?.contacts_view === 'all',
    isHimself: (state) => state.company.pivot?.contacts_view === 'himself',
    canViewOnlyHimself: (state) =>
      state.company.pivot?.contacts_view === 'himself',
    showShortTag: (state) => state.company.setting?.show_short_tag,
    avatar: (state) => state.user?.avatar,
    isObserver: (state) =>
      state.company.pivot?.contacts_view === 'all' ||
      state.company.pivot?.role === 'admin',
    allowedToFollow: (state) =>
      state.company.pivot?.contacts_view === 'all' ||
      state.company.pivot?.role === 'admin',
    showAttendantName: (state) => state.company.setting?.show_attendant_name,
    hasCompanyServiceTime: (state) =>
      state.company.setting?.service_time ?? false,
    satisfactionSurveyIsEnabled: (state) =>
      state.company.setting?.satisfaction_survey ?? false,
    forceSendSatisfactionSurvey: (state) =>
      state.company.setting?.force_satisfaction_survey ?? false,
    myDepartmentIds: (state) => state.myDepartments.map((el) => el.id),
    hasManyCompanies: (state) => state.companies.length > 1,
    allowDeleteMessage: (state) => {
      if (state.company.pivot?.role === 'admin') {
        return true
      }

      return state.company.setting?.allow_delete_messages
    },
    isInvalidSession: (state) => {
      return (currentCompanyId?: number) =>
        !isEqual(currentCompanyId, state.company?.id) &&
        state.company?.id !== undefined
    },
    getDepartmentById: (state) => {
      return (id: number) => state.departments.find((dpto) => dpto.id === id)
    },
  },
  actions: {
    toggleCompanyStatus(disabled: boolean) {
      this.company.disabled = disabled
    },
    init() {
      const q = new Queue({ results: [], autostart: true })

      this.getSettings()
      this.getCompanies(true)
      this.getDepartments(true)
      this.getTags(true)
      this.getMyDepartments(true)

      // q.push(
      //   (cb) => {
      //     useTimeoutPoll(
      //       async () => {
      //         await this.getCompanies(true)
      //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //         // @ts-ignore
      //         cb()
      //       },
      //       MilliSeconds.FIVE_MINUTES,
      //       { immediate: true },
      //     )
      //   },
      //   (cb) => {
      //     useTimeoutPoll(
      //       async () => {
      //         await this.getDepartments(true)
      //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //         // @ts-ignore
      //         cb()
      //       },
      //       MilliSeconds.FIVE_MINUTES,
      //       { immediate: true },
      //     )
      //   },
      //   (cb) => {
      //     useTimeoutPoll(
      //       async () => {
      //         await this.getTags(true)
      //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //         // @ts-ignore
      //         cb()
      //       },
      //       MilliSeconds.FIVE_MINUTES,
      //       { immediate: true },
      //     )
      //   },
      //   (cb) => {
      //     useTimeoutPoll(
      //       async () => {
      //         await this.getMyDepartments(true)
      //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //         // @ts-ignore
      //         cb()
      //       },
      //       MilliSeconds.FIVE_MINUTES,
      //       { immediate: true },
      //     )
      //   },
      // )
    },
    reset() {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.user = {}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.company = {}
      this.presenceList = []
    },
    makeSession(auth: {
      user: IPChat.Auth.User
      company: IPChat.Auth.Company
    }) {
      this.user = auth.user
      this.company = auth.company
    },
    getSettings() {
      window.axios
        .get<IPChat.CompanySetting>(route('system.resources.getSettings'))
        .then((r) => {
          this.company.setting = r.data
        })
    },
    async getCompanies(forceSync = false) {
      if (!isEmpty(this.companies) && !forceSync) {
        return this.companies
      }

      try {
        const { data } = await window.axios.get<IPChat.Company[]>(
          route('system.resources.getCompanies'),
          {
            'axios-retry': {
              retries: 0,
            },
          },
        )
        this.companies = data
      } catch (e) {
        const message =
          'Houve um erro na listagem de empresas, tente novamente!'
        logger.error(e, message)
        $toast.error(message)
      }

      return this.companies
    },
    async getDepartments(forceSync = false) {
      if (!isEmpty(this.departments) && !forceSync) {
        return this.departments
      }

      try {
        const { data } = await window.axios.get<IPChat.Department[]>(
          route('system.resources.getDepartments'),
          {
            'axios-retry': {
              retries: 0,
            },
          },
        )
        this.departments = data
      } catch (e) {
        const message =
          'Houve um erro na listagem de departamentos, tente novamente!'
        logger.error(e, message)
        $toast.error(message)
      }

      return this.departments
    },
    async getTags(forceSync = false) {
      if (!isEmpty(this.tags) && !forceSync) {
        return this.tags
      }

      try {
        const { data } = await window.axios.get<IPChat.Tag[]>(
          route('system.resources.getTags'),
          {
            'axios-retry': {
              retries: 0,
            },
          },
        )
        this.tags = data
      } catch (e) {
        const message =
          'Houve um erro na listagem de etiquetas, tente novamente!'
        logger.error(e, message)
        $toast.error(message)
      }

      return this.tags
    },
    async getMyDepartments(forceSync = false) {
      if (!isEmpty(this.myDepartments) && !forceSync) {
        return this.myDepartments
      }

      try {
        const { data } = await window.axios.get<IPChat.Department[]>(
          route('profile.myDepartments'),
          {
            'axios-retry': {
              retries: 0,
            },
          },
        )
        this.myDepartments = data
      } catch (e) {
        const message =
          'Houve um erro na listagem de departamentos do usuário, tente novamente!'
        logger.error(e, message)
        $toast.error(message)
      }

      return this.myDepartments
    },
    setPresence(user: IPChat.Auth.PresenceList) {
      const findIndex = this.presenceList.findIndex((el) => el.id === user.id)
      if (findIndex === -1) {
        this.presenceList.push(user)
      }
    },
    removePresence(user: IPChat.Auth.PresenceList) {
      const findIndex = this.presenceList.findIndex((el) => el.id === user.id)
      if (findIndex > -1) {
        this.presenceList.splice(findIndex, 1)
      }
    },
  },
})
